import { pluralize } from "@prismicio/editor-support/String"
import {
  Badge,
  Box,
  Button,
  ErrorBoundary,
  Skeleton,
  Text,
} from "@prismicio/editor-ui"
import { useSuspenseQuery } from "@tanstack/react-query"
import { Suspense } from "react"

import { getGlossaryRulesOptions } from "../../../app/services/glossary"

interface GlossarySectionProps {
  onOpenGlossaryDialog: () => void
  sourceLocaleId: string
  targetLocaleId: string
}

export function GlossarySection(props: GlossarySectionProps) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<GlossarySectionSkeleton />}>
        <GlossarySectionContent {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

function GlossarySectionContent(props: GlossarySectionProps) {
  const { onOpenGlossaryDialog, sourceLocaleId, targetLocaleId } = props

  const { data: glossaryRules } = useSuspenseQuery(
    getGlossaryRulesOptions({
      sourceLocaleId,
      targetLocaleId,
    }),
  )

  const isGlossaryEmpty = glossaryRules.length === 0

  return (
    <>
      <Box flexDirection="column" gap={4}>
        <Box alignItems="center" justifyContent="space-between" gap={16}>
          <GlossarySectionTitle />
          {isGlossaryEmpty ? (
            <Text variant="small" color="grey11">
              No glossary rules defined yet
            </Text>
          ) : (
            <Text variant="small" color="grey11">
              {glossaryRules.length}{" "}
              {pluralize(glossaryRules.length, "glossary rule")} defined
            </Text>
          )}
        </Box>

        <Button
          onClick={onOpenGlossaryDialog}
          size="large"
          startIcon="add"
          color="grey"
        >
          {isGlossaryEmpty ? "Add a first rule" : "Manage or add rules"}
        </Button>

        <GlossarySectionDescription />
      </Box>
    </>
  )
}

function GlossarySectionTitle() {
  return (
    <Box gap={4} alignItems="center">
      <Badge title="New" size="medium" color="purple" />
      <Text variant="emphasized">Glossary</Text>
    </Box>
  )
}

function GlossarySectionDescription() {
  return (
    <Text variant="small" color="grey11">
      Define how specific terms should be translated to stay on-brand.
    </Text>
  )
}

function GlossarySectionSkeleton() {
  return (
    <Box flexDirection="column" gap={4}>
      <Box alignItems="center" justifyContent="space-between" gap={16}>
        <GlossarySectionTitle />
        <Skeleton width={165} height={16} borderRadius={4} />
      </Box>

      <Skeleton width="100%" height={40} />

      <GlossarySectionDescription />
    </Box>
  )
}
